import React, { useState, useEffect } from 'react';
import { getProducts, getThemes, createProduct, updateProduct, deleteProduct, toggleProductVisibility } from '../../api/adminApi';
import Modal from '../../components/admin/Modal';
import ConfirmDialog from '../../components/admin/ConfirmDialog';

function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [themes, setThemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, product: null });

  // Form state
  const [formData, setFormData] = useState({
    theme_id: '',
    name: '',
    display_order: 0,
    is_visible: true
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [productsData, themesData] = await Promise.all([
        getProducts(),
        getThemes()
      ]);
      setProducts(productsData);
      setThemes(themesData);
    } catch (err) {
      console.error('Error fetching data:', err);
      alert('Erreur de chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingProduct(null);
    setFormData({
      theme_id: themes[0]?.id || '',
      name: '',
      display_order: 0,
      is_visible: true
    });
    setImageFile(null);
    setImagePreview(null);
    setIsModalOpen(true);
  };

  const openEditModal = (product) => {
    setEditingProduct(product);
    setFormData({
      theme_id: product.theme_id,
      name: product.name,
      display_order: product.display_order || 0,
      is_visible: product.is_visible
    });
    setImageFile(null);
    setImagePreview(product.image_path);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingProduct(null);
    setImageFile(null);
    setImagePreview(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('theme_id', formData.theme_id);
      formDataToSend.append('name', formData.name);
      formDataToSend.append('display_order', formData.display_order);
      formDataToSend.append('is_visible', formData.is_visible ? 1 : 0);

      if (imageFile) {
        formDataToSend.append('image', imageFile);
        const theme = themes.find(t => t.id === parseInt(formData.theme_id));
        formDataToSend.append('themeSlug', theme?.slug || 'general');
      }

      if (editingProduct) {
        await updateProduct(editingProduct.id, formDataToSend);
      } else {
        await createProduct(formDataToSend);
      }

      await fetchData();
      closeModal();
    } catch (err) {
      console.error('Error saving product:', err);
      alert('Erreur lors de l\'enregistrement');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (product) => {
    setDeleteDialog({ isOpen: true, product });
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteProduct(deleteDialog.product.id);
      setProducts(products.filter(p => p.id !== deleteDialog.product.id));
      setDeleteDialog({ isOpen: false, product: null });
    } catch (err) {
      console.error('Error deleting product:', err);
      alert('Erreur lors de la suppression');
    }
  };

  const handleToggleVisibility = async (productId) => {
    try {
      await toggleProductVisibility(productId);
      await fetchData();
    } catch (err) {
      console.error('Error toggling visibility:', err);
      alert('Erreur lors du changement de visibilité');
    }
  };

  if (loading) {
    return <div>Chargement...</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h1 style={{ color: '#2c3e50' }}>Produits ({products.length})</h1>
        <button
          onClick={openCreateModal}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: '#9c6f44',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: 600
          }}
        >
          + Nouveau produit
        </button>
      </div>

      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Image</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Nom</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Thème</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Disponibilité</th>
              <th style={{ padding: '1rem', textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {products.length === 0 ? (
              <tr>
                <td colSpan="5" style={{ padding: '2rem', textAlign: 'center', color: '#7f8c8d' }}>
                  Aucun produit trouvé
                </td>
              </tr>
            ) : (
              products.map(product => (
                <tr key={product.id} style={{
                  borderBottom: '1px solid #dee2e6',
                  opacity: product.is_visible ? 1 : 0.6,
                  backgroundColor: product.is_visible ? 'transparent' : '#f8f9fa'
                }}>
                  <td style={{ padding: '1rem' }}>
                    {product.image_path && (
                      <img
                        src={product.image_path}
                        alt={product.name}
                        style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '4px' }}
                      />
                    )}
                  </td>
                  <td style={{ padding: '1rem', fontWeight: 500 }}>{product.name}</td>
                  <td style={{ padding: '1rem', color: '#7f8c8d' }}>{product.theme_name}</td>
                  <td style={{ padding: '1rem' }}>
                    <button
                      onClick={() => handleToggleVisibility(product.id)}
                      style={{
                        padding: '0.25rem 0.75rem',
                        backgroundColor: product.is_visible ? '#d4edda' : '#f8d7da',
                        color: product.is_visible ? '#155724' : '#721c24',
                        border: 'none',
                        borderRadius: '12px',
                        fontSize: '0.875rem',
                        cursor: 'pointer',
                        fontWeight: 500,
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                      onMouseLeave={(e) => e.target.style.opacity = '1'}
                    >
                      {product.is_visible ? '✓ Disponible' : '✗ Indisponible'}
                    </button>
                  </td>
                  <td style={{ padding: '1rem', textAlign: 'right' }}>
                    <button
                      onClick={() => openEditModal(product)}
                      style={{
                        padding: '0.5rem 1rem',
                        marginRight: '0.5rem',
                        backgroundColor: '#3498db',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      Éditer
                    </button>
                    <button
                      onClick={() => handleDeleteClick(product)}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: '#e74c3c',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      Supprimer
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={editingProduct ? 'Modifier le produit' : 'Nouveau produit'}
      >
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
              Thème *
            </label>
            <select
              name="theme_id"
              value={formData.theme_id}
              onChange={handleInputChange}
              required
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
            >
              {themes.map(theme => (
                <option key={theme.id} value={theme.id}>{theme.name}</option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
              Nom *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
              Image
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
            />
            {imagePreview && (
              <img
                src={imagePreview}
                alt="Preview"
                style={{
                  marginTop: '1rem',
                  maxWidth: '200px',
                  maxHeight: '200px',
                  objectFit: 'cover',
                  borderRadius: '4px'
                }}
              />
            )}
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="checkbox"
                name="is_visible"
                checked={formData.is_visible}
                onChange={handleInputChange}
                style={{ marginRight: '0.5rem' }}
              />
              <span>Disponible sur le site</span>
            </label>
          </div>

          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
            <button
              type="button"
              onClick={closeModal}
              style={{
                padding: '0.5rem 1.5rem',
                backgroundColor: '#95a5a6',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={submitting}
              style={{
                padding: '0.5rem 1.5rem',
                backgroundColor: '#9c6f44',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: submitting ? 'not-allowed' : 'pointer',
                opacity: submitting ? 0.7 : 1
              }}
            >
              {submitting ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteDialog({ isOpen: false, product: null })}
        title="Confirmer la suppression"
        message={`Êtes-vous sûr de vouloir supprimer "${deleteDialog.product?.name}" ?`}
      />
    </div>
  );
}

export default ProductsPage;
